import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import {
  Dimensions,
  ImageCropperComponent,
  ImageTransform,
  LoadedImage,
} from "ngx-image-cropper";
import { Observable } from "rxjs";
import { EventService } from "src/app/core/services/event.service";
import { PropertiesService } from "../../../services/properties.services";
import {
  CropFillMode,
  CropRect,
  CropRotation,
  CropperPosition,
  PUBLISHED_ASPECT,
  cropLoss,
  drawFitPreview,
  fillModeFromCropMode,
  loadImageElement,
  normalizeRotation,
  shouldFitWhole,
  toCropperPosition,
  toRelativeCrop,
  zoomCap,
} from "./crop-math";

/**
 * Largura da pré-visualização. Era 500 px (o card da galeria); ampliada no
 * painel ficava a ser esticada. 1000 px cobre o painel expandido sem pesar.
 */
const PREVIEW_WIDTH = 1000;

/**
 * Editor da imagem do imóvel sobre o **master** (imagem inteira, ≤ 2000 px),
 * nunca sobre a publicada. A publicada é sempre 16:9; aqui escolhe-se como
 * lá chegar: "Recortar" (moldura 16:9), "Inteira · desfocado" ou "Inteira ·
 * branco" (moldura livre, só apara margens; o servidor preenche as laterais).
 * Ao gravar emite só coordenadas relativas (`crop-image` com
 * `{ id, crop, cropPreview }`); o servidor regenera a publicada ao guardar a
 * ficha. Nada é recortado no browser.
 *
 * O modo inicial é o guardado; numa imagem nova, o que o automático faria
 * (recorte ao centro, ou inteira desfocada quando o recorte perderia > 30%).
 *
 * Origem da imagem: `originalFile` (imagem nova), rota autenticada do
 * master (imagem guardada) ou a publicada (imagem anterior ao recorte real,
 * com zoom limitado a 1×).
 */
@Component({
  selector: "app-cropperr",
  templateUrl: "./cropperr.component.html",
  styleUrls: ["./cropperr.component.scss"],
})
export class CropperrComponent implements AfterViewInit, OnDestroy {
  image?: File;
  cropImgPreview: any;
  @Input()
  imgData: any;
  scale = 1;
  maxScale = 1;
  transform: ImageTransform = {};
  /** Número de rotações de 90° (0..3) aplicadas pelo cropper. */
  canvasRotation = 0;
  cropper: CropperPosition = { x1: 0, y1: 0, x2: 0, y2: 0 };
  isLoadImg = false;
  isLegacy = false;
  /** Como a publicada chega ao 16:9. */
  mode: CropFillMode = "crop";
  /** Painel aberto (o painel só existe no DOM enquanto o está). */
  isOpen = false;
  /** Pré-visualização ampliada por toque/clique (o rato por cima amplia por CSS). */
  previewExpanded = false;
  readonly publishedAspect = PUBLISHED_ASPECT;
  /** Dentro do `*ngIf="isOpen"`: só existe com o painel aberto. */
  @ViewChild(ImageCropperComponent)
  imageCropper?: ImageCropperComponent;

  /** Evento emitido ao gravar/repor (o cropper de empreendimentos usa outro). */
  protected cropEventName = "crop-image";
  /** Recorte guardado a aplicar na próxima `cropperReady` (só na abertura). */
  private initialCrop: CropRect | null = null;
  /** Dimensões da imagem rodada tal como o cropper a carregou. */
  private imageWidth = 0;
  private imageHeight = 0;
  /** Descarta pré-visualizações assíncronas que chegam depois de outra. */
  private previewToken = 0;

  constructor(
    protected eventService: EventService,
    protected propertiesService: PropertiesService,
    private host: ElementRef<HTMLElement>,
  ) {}

  /**
   * O cropper é declarado dentro do ngx-simplebar da Multimédia, cujo
   * `.simplebar-mask` tem `z-index: 0`: o painel (e o backdrop) ficavam
   * presos por baixo do cabeçalho/rodapé fixos do modal do imóvel. Enquanto
   * o componente existir vive no `body`; as ligações do Angular não dependem
   * da posição no DOM.
   */
  ngAfterViewInit(): void {
    document.body.appendChild(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    // Fora da árvore original, o Angular já não o remove com o modal.
    this.host.nativeElement.remove();
  }

  @HostListener("document:keydown.escape")
  onEscape() {
    if (this.isOpen) this.close();
  }

  protected loadMasterImage(fileName: string): Observable<Blob> {
    return this.propertiesService.getMasterImage(fileName);
  }

  protected loadPublishedImage(url: string): Observable<Blob> {
    return this.propertiesService.getImage(url);
  }

  get rotation(): CropRotation {
    return normalizeRotation(this.canvasRotation * 90);
  }

  get isFit(): boolean {
    return this.mode !== "crop";
  }

  /** Quanto da imagem (rodada) um recorte 16:9 perde, em percentagem inteira. */
  get cropLossPercent(): number {
    return Math.round(cropLoss(this.imageWidth, this.imageHeight) * 100);
  }

  /** A imagem já é 16:9: "inteira" e "recortar" dão o mesmo resultado. */
  get isAlready169(): boolean {
    return this.cropLossPercent === 0;
  }

  open(imgData: any) {
    this.isOpen = true;
    this.isLoadImg = true;
    this.previewExpanded = false;
    this.image = undefined;
    this.cropImgPreview = undefined;
    this.imgData = imgData;
    this.isLegacy = !imgData?.isNew && !imgData?.hasMaster;
    // Só uma escolha feita no cropper (manual/fit/fill) é reposta tal como
    // está. Um recorte automático guardado (auto/autofit) pode vir de uma
    // regra antiga: ignora-se e o painel abre como o automático de hoje
    // faria — modo pela perda do recorte 16:9 e moldura ao máximo.
    const savedMode = `${imgData?.cropMode ?? ""}`;
    const isManualChoice = ["manual", "fit", "fill"].includes(savedMode);
    this.initialCrop = isManualChoice ? imgData?.crop ?? null : null;
    // Imagem anterior ao recorte real: a publicada já é um 16:9 recortado,
    // não há imagem inteira a recuperar.
    this.mode = this.isLegacy
      ? "crop"
      : isManualChoice
        ? fillModeFromCropMode(savedMode)
        : shouldFitWhole(imgData?.masterWidth, imgData?.masterHeight)
          ? "blur"
          : "crop";
    this.canvasRotation = this.initialCrop
      ? normalizeRotation(this.initialCrop.rotation) / 90
      : 0;
    this.resetZoom();

    if (imgData?.isNew && imgData?.originalFile) {
      this.setImage(imgData.originalFile, imgData.name);
    } else if (imgData?.hasMaster && imgData?.fileName) {
      this.loadMasterImage(imgData.fileName).subscribe(
        (blob) => this.setImage(blob, imgData.fileName),
        (err) => {
          console.warn("Master indisponível; a usar a publicada", err);
          this.isLegacy = true;
          this.mode = "crop";
          this.loadPublished(imgData);
        },
      );
    } else {
      this.loadPublished(imgData);
    }

    document.body.classList.add("crop-panel-open");
    document
      .getElementById("btn-property-modal-footer")
      ?.classList.add("d-none");
  }

  private loadPublished(imgData: any) {
    const url = imgData?.img?.changingThisBreaksApplicationSecurity ?? imgData?.img;
    if (!url) {
      this.isLoadImg = false;
      return;
    }
    this.loadPublishedImage(String(url)).subscribe(
      (blob) => this.setImage(blob, imgData?.fileName || imgData?.name),
      (err) => {
        console.error(err);
        this.isLoadImg = false;
      },
    );
  }

  private setImage(source: File | Blob, name?: string) {
    this.image =
      source instanceof File
        ? source
        : new File([source], name || "image", {
            type: source?.type || "image/jpeg",
          });
  }

  close() {
    this.isOpen = false;
    this.isLoadImg = false;
    this.resetZoom();
    this.image = undefined;
    this.cropImgPreview = undefined;
    document.body.classList.remove("crop-panel-open");
    document
      .getElementById("btn-property-modal-footer")
      ?.classList.remove("d-none");
  }

  onImageLoaded(loaded: LoadedImage) {
    this.imageWidth = loaded?.transformed?.size?.width ?? 0;
    this.imageHeight = loaded?.transformed?.size?.height ?? 0;
    // Imagem anterior ao recorte real: só a publicada, sem margem para ampliar.
    this.maxScale = this.isLegacy ? 1 : zoomCap(this.imageWidth);
    if (this.scale > this.maxScale) this.setScale(this.maxScale);
  }

  onCropperReady(displayed: Dimensions) {
    if (this.initialCrop && displayed?.width && displayed?.height) {
      this.cropper = toCropperPosition(this.initialCrop, displayed);
      this.initialCrop = null;
    }
    this.isLoadImg = false;
    this.refreshPreview();
  }

  onLoadImageFailed() {
    console.error("Falha ao carregar a imagem no cropper");
    this.isLoadImg = false;
  }

  /**
   * Pré-visualização (1000 px) da moldura actual; serve o painel e o card.
   * Nos modos inteira é composta como o servidor a publica.
   */
  refreshPreview() {
    const token = ++this.previewToken;
    const output = this.imageCropper?.crop();
    if (!output?.base64) return;
    if (!this.isFit) {
      this.cropImgPreview = output.base64;
      return;
    }
    this.composeFitPreview(output.base64).then((preview) => {
      if (token === this.previewToken) this.cropImgPreview = preview;
    });
  }

  private async composeFitPreview(base64: string): Promise<string> {
    try {
      const img = await loadImageElement(base64);
      const fill = this.mode === "white" ? "white" : "blur";
      return drawFitPreview(img, img.naturalWidth, img.naturalHeight, fill, PREVIEW_WIDTH).toDataURL(
        "image/jpeg",
        0.92,
      );
    } catch {
      return base64;
    }
  }

  /**
   * Troca de modo. Entre os dois modos "inteira" só muda o preenchimento: a
   * moldura fica. De/para "recortar" o cropper repõe a moldura (muda
   * `maintainAspectRatio`); em inteira passa a ser a imagem toda.
   */
  setMode(mode: CropFillMode) {
    if (this.mode === mode) return;
    const wasFit = this.isFit;
    this.mode = mode;
    if (wasFit === this.isFit) {
      this.refreshPreview();
      return;
    }
    this.initialCrop = null;
    setTimeout(() => {
      this.imageCropper?.resetCropperPosition();
      this.refreshPreview();
    });
  }

  onCropperChange() {
    this.refreshPreview();
  }

  private setScale(value: number) {
    this.scale = Math.min(this.maxScale, Math.max(1, Number(value.toFixed(2))));
    this.transform = { ...this.transform, scale: this.scale };
    this.refreshPreview();
  }

  private resetZoom() {
    this.scale = 1;
    this.transform = { ...this.transform, scale: 1 };
  }

  get canZoomIn(): boolean {
    return this.scale < this.maxScale - 1e-6;
  }

  get canZoomOut(): boolean {
    return this.scale > 1 + 1e-6;
  }

  zoomOut() {
    if (this.canZoomOut) this.setScale(this.scale - 0.05);
  }

  zoomIn() {
    if (this.canZoomIn) this.setScale(this.scale + 0.05);
  }

  rotateLeft() {
    this.rotate(-1);
  }

  rotateRight() {
    this.rotate(1);
  }

  private rotate(steps: number) {
    // O cropper recarrega a imagem rodada e volta a emitir `cropperReady`
    // com a moldura reposta ao máximo: o recorte guardado deixa de se aplicar.
    this.initialCrop = null;
    this.canvasRotation = (((this.canvasRotation + steps) % 4) + 4) % 4;
    this.resetZoom();
  }

  async saveImage() {
    const output = this.imageCropper?.crop();
    if (!output?.imagePosition || !this.imageWidth || !this.imageHeight) {
      this.close();
      return;
    }
    const relative = toRelativeCrop(
      output.imagePosition,
      this.imageWidth,
      this.imageHeight,
      this.rotation,
      this.scale,
    );
    const crop: CropRect =
      this.mode === "white"
        ? { ...relative, fit: true, white: true }
        : this.mode === "blur"
          ? { ...relative, fit: true }
          : relative;
    const cropPreview = output.base64
      ? this.isFit
        ? await this.composeFitPreview(output.base64)
        : output.base64
      : null;
    this.eventService.broadcast(this.cropEventName, {
      id: this.imgData?.id,
      crop,
      cropPreview,
    });
    this.close();
  }

  /** Volta ao recorte automático do servidor (aplicado ao guardar a ficha). */
  resetCrop() {
    this.eventService.broadcast(this.cropEventName, {
      id: this.imgData?.id,
      crop: null,
      cropPreview: null,
    });
    this.close();
  }

  onMouseWheel(event: any) {
    event.preventDefault();
    const delta = event.deltaY || event.detail || event?.wheelDelta;
    if (delta > 0) {
      this.zoomOut();
    } else if (delta < 0) {
      this.zoomIn();
    }
  }
}
